import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect } from 'react';
import ImageResize from 'tiptap-extension-resize-image';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { AddCircleIcons } from '../../icons/icons';
import EditorToolbar from './EditorToolbar';

interface RichTextEditorProps {
  jsonStringTemplate: string;
  validVariables?: { name: string; variable: string }[];
  saveTemplate: (template: string, jsonTemplate: string) => void;
  value?: string;
  onEditorReady?: (editor: any) => void;
  buttonText?: string;
  hideSaveButton?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  jsonStringTemplate,
  validVariables,
  saveTemplate,
  value,
  onEditorReady,
  buttonText = 'Save',
  hideSaveButton = false,
}) => {
  const VARIABLES = ['applicant_name', 'job_title_name', 'end_of_assessment'];
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Write something...' }),
      Image.configure({
        allowBase64: true,
      }),
      ImageResize,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
    ],
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[360px] [&_ol]:list-decimal [&_ul]:list-disc rounded-lg border border-slate-200 bg-white p-3 text-slate-700 outline-none focus:ring-2 focus:ring-emerald-100',
      },
    },
    content: value ? value : JSON.parse(jsonStringTemplate),
  });

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  const handleInsertVariable = useCallback(
    (variable: string) => {
      if (editor && variable.length !== 0) {
        const variableText = VARIABLES.includes(variable)
          ? `{{${variable}}}`
          : variable;
        editor.chain().focus().insertContent(variableText).run();
      }
    },
    [editor],
  );

  const handleInsertImage = useCallback(
    (variable: string) => {
      if (editor) {
        editor.chain().focus().setImage({ src: variable }).run();
      }
    },
    [editor],
  );

  const setLink = useCallback(
    (url: string) => {
      if (editor) {
        if (url === null) {
          return;
        }

        if (url === '') {
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
          return;
        }

        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: `{{${url}}}` })
          .run();
      }
    },
    [editor],
  );

  const exportToHTML = () => {
    if (editor) {
      const html = editor.getHTML();
      const htmlJson = JSON.stringify(editor.getJSON());
      if (jsonStringTemplate !== htmlJson) {
        saveTemplate(html, htmlJson);
      }
    }
  };

  const handleVariableClick = (variableName: string, variable: string) => {
    switch (variableName) {
      case 'Company Logo':
        return handleInsertImage(variable);
      case 'Assessment Link':
        return setLink(variable);
      case 'Unset Link':
        return editor && editor.chain().focus().unsetLink().run();
      default:
        return handleInsertVariable(variable);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        p: 2,
      }}
    >
      <EditorToolbar editor={editor} />
      {!hideSaveButton && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            borderTop: '1px solid',
            borderColor: 'divider',
            pt: 1.5,
            pb: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={exportToHTML}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {buttonText}
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 260px' },
          gap: 2,
          mt: 1,
        }}
      >
        <Box sx={{ minWidth: 0, order: { xs: 2, lg: 1 }, flexGrow: 1 }}>
          <EditorContent editor={editor} />
        </Box>
        <Box
          component="aside"
          sx={{
            minWidth: 0,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            p: 2,
            order: { xs: 1, lg: 2 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 700, color: 'text.primary' }}
          >
            Insert Variables
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: '1fr' },
              gap: 1,
            }}
          >
            {validVariables &&
              validVariables.map((variable) => (
                <Button
                  key={variable.variable}
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    handleVariableClick(variable.name, variable.variable)
                  }
                  startIcon={<AddCircleIcons />}
                  sx={{
                    justifyContent: 'flex-start',
                    borderRadius: '8px',
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                    borderColor: 'primary.light',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    py: 0.75,
                    px: 1.25,
                    '&:hover': {
                      bgcolor: 'primary.50',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {variable.name}
                </Button>
              ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default RichTextEditor;
